import { Component } from '@angular/core';
import {
    ChartConfigs,
    ChartVisualizationType,
    FieldMetadata,
    TooltipConfigItem,
    VizConfigs,
} from '@ddv/models';

import { ConfigurationZoneChange } from '../../../models/configuration-zone-change';
import { ChartDropZoneConfig } from '../../../models/drop-zone-config';
import { ConfigureComponent } from '../configure/configure.component';
import * as dropZoneConfig from '../drop-zone-config';

@Component({
    selector: 'app-mw-configure',
    templateUrl: './configure-charts.component.html',
    styleUrls: ['../configure/configure.component.scss'],
    standalone: false,
})
export class ConfigureChartsComponent extends ConfigureComponent {
    override dropZones: ChartDropZoneConfig[] = [];
    override dataConfigs: ChartConfigs = { values: [], slicers: [], tooltips: [] };
    protected override visualizationType: ChartVisualizationType | undefined;

    dragStarted(data: FieldMetadata): void {
        this.dropZones.forEach((item) => {
            const isNotFull = (this.dataConfigs[item.configKey]?.length ?? 0) < item.maximumAllowed!;
            const allowDuplicate = item.allowDuplicate ??
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                !this.dataConfigs[item.configKey]?.some((config: any) => config.value === data.name);
            const isValidDatatype = item.dataTypes?.indexOf(data.datatype ?? '') !== -1;
            if (item.allowed) {
                item.allowed = isNotFull && allowDuplicate && isValidDatatype;
            }
        });
    }

    override initDataConfigs(): void {
        this.dropZones.forEach((zone) => {
            this.dataConfigs[zone.configKey] = [];
        });
    }

    override hasRequiredFields(): boolean {
        return !this.dropZones.some((zone) => zone.mandatory && !this.dataConfigs[zone.configKey]?.length);
    }

    override getSelectedConfigs(): ChartConfigs {
        return this.dataConfigs;
    }

    protected getDropZones(): ChartDropZoneConfig[] {
        switch (this.visualizationType) {
            case 'LINE_CHART':
                return dropZoneConfig.line;
            case 'STACKED_AREA_CHART':
                return dropZoneConfig.stackedArea;
            case 'VERTICAL_STACKED_BAR_CHART':
            case 'HORIZONTAL_STACKED_BAR_CHART':
                return dropZoneConfig.stackedBar;
            case 'VERTICAL_MIRRORED_BAR_CHART':
                return dropZoneConfig.mirroredBar;
            default:
                return dropZoneConfig.defaultConfig;
        }
    }

    protected setDataConfigs(configs: ChartConfigs): void {
        this.dataConfigs = configs;
    }

    protected afterMetadataUpdate(): void {
        this.addDisplayTypeInDataConfig();
    }

    protected override dragEnded(): void {
        this.dropZones.forEach((item) => {
            item.allowed = true;
        });

        if (this.visualizationType === 'LINE_CHART') {
            this.updateLineChartDropzonePermissions();
        }
    }

    protected onConfigurationZoneChange({ dataConfigs, key }: ConfigurationZoneChange): void {
        (this.dataConfigs[key] as unknown) = dataConfigs;

        if (this.visualizationType === 'LINE_CHART') {
            this.updateLineChartDropzonePermissions();
        }

        this.renderPreview();
    }

    protected onConfigurationFieldDrop(configItem: TooltipConfigItem): void {
        const ttpZone = this.dropZones.find((item) => item.id === 'tooltip');
        if (ttpZone?.allowed) {
            const tooltips = this.dataConfigs.tooltips;
            const index = tooltips.length;

            this.dataConfigs.tooltips = [...tooltips.slice(0, index), configItem, ...tooltips.slice(index)];
        }
    }

    private addDisplayTypeInDataConfig(): void {
        (Object.keys(this.dataConfigs) as (keyof VizConfigs)[]).forEach((key) => {
            const columns = this.dataConfigs[key];
            if (!columns) {
                return;
            }

            columns.forEach((column) => {
                const columnMetaData = this.metadata.find((colMetadata) => colMetadata.name === column.value);
                column.displayType = columnMetaData?.displayType ?? 'string';
            });
        });
    }

    private updateLineChartDropzonePermissions(): void {
        const sliserZone = this.dropZones.find((zone) => zone.id === 'lslicer');
        const valueZone = this.dropZones.find((zone) => zone.id === 'lvalue');

        if (sliserZone && valueZone) {
            const valuesLength = this.dataConfigs.values.length;
            sliserZone.allowed = valuesLength < (valueZone.maximumAllowed ?? 2);
            valueZone.allowed = this.dataConfigs.slicers.length === 0 || !valuesLength;
        }
    }
}
